import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import type { HiPayCardInstance, PaymentFormValues } from 'Src/types/payment';
import React, { useEffect, useRef, useState } from 'react';
import { oidc } from '@clubmed/utils';
import config from 'Config/index';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { HostedField } from 'Components/widgets/payment-info/payment-form/HostedField';
import { Title } from 'Components/frameworks/title/Title';
import { t } from 'Src/i18n';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { PaymentFormikInput } from 'Components/widgets/payment-info/paymentFormik';
const HIPAY_FIELD_FULL_NAME = 'hipay-card-holder';
const HIPAY_FIELD_CARD_NUMBER = 'hipay-card-number';
const HIPAY_FIELD_EXPIRY_DATE = 'hipay-card-expiry';
const HIPAY_FIELD_CVC = 'hipay-card-cvc';
export const HipayForm = () => {
  const formik = useFormikContext<PaymentFormValues>();
  const { scriptLoaded } = useScriptLoader(config.get('psp.mhipay.script_url') as string);
  const appContext = useAppContext();
  const [errors, setErrors] = useState({
    cardNumber: '',
    cvc: '',
    expiryDate: '',
    cardHolder: '',
  });
  const { isOpen: isHipayFormReady, onOpen: setIsHipayFormReady } = useDisclosure();
  const hipayCardCreated = useRef<HiPayCardInstance | Record<string, never>>({});

  const setHipayErrorsAsObject = (hipayErrors: ReadonlyArray<{ field: string; error: string }>) => {
    setErrors(
      hipayErrors.reduce((acc, { field, error }) => ({ ...acc, [field]: error }), {
        cardNumber: '',
        cvc: '',
        expiryDate: '',
        cardHolder: '',
      }),
    );
  };

  useEffect(() => {
    if (formik.isSubmitting && isHipayFormReady && !formik.values.token) {
      (hipayCardCreated.current as HiPayCardInstance)
        .getPaymentData()
        .catch(setHipayErrorsAsObject);
    }
  }, [formik.isSubmitting, isHipayFormReady]);
  useEffect(() => {
    if (scriptLoaded && window.HiPay) {
      // eslint-disable-next-line new-cap
      const hipay = window.HiPay({
        environment: config.get('psp.mhipay.environment'),
        password: config.get('psp.mhipay.password'),
        username: config.get('psp.mhipay.username'),
      });
      hipayCardCreated.current = hipay.create('card', {
        multi_use: isFeatureFlipped(appContext)('psp.tokenize.mhipay'),
        fields: {
          cardHolder: {
            placeholder: t('common.payment.hipay.placeholderFullName'),
            selector: `${HIPAY_FIELD_FULL_NAME}`,
          },
          cardNumber: {
            placeholder: t('common.payment.hipay.placeholderCardNumber'),
            selector: `${HIPAY_FIELD_CARD_NUMBER}`,
          },
          cvc: {
            placeholder: t('common.payment.hipay.placeholderCvc'),
            selector: `${HIPAY_FIELD_CVC}`,
          },
          expiryDate: {
            placeholder: t('common.payment.hipay.placeholderExpiryDate'),
            selector: `${HIPAY_FIELD_EXPIRY_DATE}`,
          },
        },
      });
      hipayCardCreated.current.on('ready', setIsHipayFormReady);
      hipayCardCreated.current.on('change', async (payload: unknown) => {
        const { valid, errors: hipayErrors } = payload as {
          valid: boolean;
          errors: ReadonlyArray<{ field: string; error: string }>;
        };
        setHipayErrorsAsObject(hipayErrors);

        if (!valid) {
          return formik.setFieldValue('token', '');
        }

        const data = await hipayCardCreated.current.getPaymentData();

        /**
         * Even if browserInfo is not related to uri state or oidc, this helper is very handy in this case
         * to store it as form field and send the object to gql as string
         * */
        formik.setFieldValue('browserInfo', oidc.toUriEncodedState(data.browser_info));
        formik.setFieldValue('paymentProduct', data.payment_product);
        formik.setFieldValue('token', data.token);
      });
    }
  }, [scriptLoaded]);
  return (
    <div data-testid="Payment-Credit-Card-Block">
      <PaymentFormikInput isRequired name="browserInfo" type="hidden" />
      <PaymentFormikInput isRequired name="token" type="hidden" />
      <PaymentFormikInput name="paymentProduct" type="hidden" />
      <Title
        className={classnames('mb-10', {
          'formik-errored': !formik.values.token,
        })}
        theme="title-brand-n2"
      >
        {t('common.payment.hipay.cardDetailTitle')}
      </Title>
      <Capsule header="none">
        <div className="flex flex-wrap px-0 py-20">
          <HostedField
            id={`${HIPAY_FIELD_FULL_NAME}`}
            label={t('common.payment.hipay.fullNameLabel')}
            errorMessage={errors.cardHolder}
            hasError={!!errors.cardHolder}
          />
          <HostedField
            id={`${HIPAY_FIELD_CARD_NUMBER}`}
            label={t('common.payment.hipay.cardNumberLabel')}
            errorMessage={errors.cardNumber}
            hasError={!!errors.cardNumber}
          />
          <HostedField
            id={`${HIPAY_FIELD_EXPIRY_DATE}`}
            label={t('common.payment.hipay.expiryDateLabel')}
            errorMessage={errors.expiryDate}
            hasError={!!errors.expiryDate}
          />
          <HostedField
            id={`${HIPAY_FIELD_CVC}`}
            label={t('common.payment.hipay.cvcLabel')}
            errorMessage={errors.cvc}
            hasError={!!errors.cvc}
            small
          />
        </div>
      </Capsule>
    </div>
  );
};
